import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import useAuthStore from '../stores/authStore';
import { Button, Input } from '../components/ui';

const loginSchema = z.object({
	email: z.string().email('Invalid email address'),
	password: z.string().min(6, 'Password must be at least 6 characters'),
	rememberMe: z.boolean().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;

const Login = () => {
	const { login } = useAuthStore();
	const [showPassword, setShowPassword] = useState(false);
	const [isLoggingIn, setIsLoggingIn] = useState(false);

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<LoginFormData>({
		resolver: zodResolver(loginSchema),
		defaultValues: {
			rememberMe: false,
		},
	});

	const onSubmit = async (data: LoginFormData) => {
		setIsLoggingIn(true);
		const result = await login(data.email, data.password, data.rememberMe);

		if (result.success) {
			toast.success('Login successful!');
			// Keep isLoggingIn true - navigation is handled by Router
		} else {
			setIsLoggingIn(false);
			toast.error(result.error || 'Invalid email or password');
		}
	};

	return (
		<div
			className="min-h-screen flex items-center justify-center transition-colors"
			style={{
				background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
			}}
		>
			<div
				className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-[420px] py-12 px-10 m-5 transition-colors"
				style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
			>
				<div className="text-center mb-8">
					<h1
						className="text-[28px] font-bold bg-clip-text text-transparent mb-2"
						style={{
							backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
						}}
					>
						Beauty Business Portal
					</h1>
					<p className="text-sm text-gray-500 dark:text-gray-400">
						Sign in to your account
					</p>
				</div>

				<form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
					<Input
						type="email"
						label="Email Address"
						placeholder="Enter your email"
						error={errors.email?.message}
						autoComplete="email"
						{...register('email')}
					/>

					<div className="relative">
						<Input
							type={showPassword ? 'text' : 'password'}
							label="Password"
							placeholder="Enter your password"
							error={errors.password?.message}
							autoComplete="current-password"
							{...register('password')}
						/>
						<button
							type="button"
							className="absolute right-3 top-9 text-gray-400 dark:text-gray-500 hover:text-blue-700 dark:hover:text-blue-400 transition-colors text-xl p-1"
							onClick={() => setShowPassword(!showPassword)}
						>
							👁️
						</button>
					</div>

					<div className="flex items-center justify-between text-sm">
						<label className="flex items-center gap-2 cursor-pointer">
							<input
								type="checkbox"
								className="w-[18px] h-[18px] cursor-pointer rounded"
								{...register('rememberMe')}
							/>
							<span className="text-gray-700 dark:text-gray-300">Remember me</span>
						</label>
						<a
							href="#"
							className="text-blue-700 dark:text-blue-400 hover:underline font-semibold"
						>
							Forgot password?
						</a>
					</div>

					<Button
						type="submit"
						variant="primary"
						className="w-full"
						size="lg"
						isLoading={isLoggingIn}
					>
						Sign In
					</Button>
				</form>

				{/* Divider */}
				<div className="flex items-center my-8 text-gray-500 dark:text-gray-400 text-sm">
					<div className="flex-1 h-px bg-gray-200 dark:bg-gray-600"></div>
					<span className="px-4">OR</span>
					<div className="flex-1 h-px bg-gray-200 dark:bg-gray-600"></div>
				</div>

				<div className="text-center">
					<a
						href="#"
						className="text-blue-700 dark:text-blue-400 font-semibold text-sm hover:underline"
					>
						Need help signing in?
					</a>
				</div>

				<div className="mt-6 text-center text-[13px] text-gray-500 dark:text-gray-400">
					© 2025 Beauty Business Portal. All rights reserved.
				</div>
			</div>
		</div>
	);
};

export default Login;
